import { CommerceSettingsView } from "./CommerceSettings";
import { useEffect, useMemo, useRef, useState } from "react";
import type { ShopSettings } from "@stackje/site-builder";
import type { ShopApi, ShopIndex } from "../../api/contract";
import { ShopRefusedError } from "../../api/errors";
import { shopRequest, ShopTimeoutError } from "./shopRequest";
import { ShopActivity } from "./ShopLoading";
import { AreaError, Card, Combobox, Field, ListHead, SaveBar } from "../area";
import { currencyOptions, likelyCurrency } from "../../lib/shopLocale";


interface ShopSettingsViewProps {
  api: ShopApi;
  siteId: string;
  shop: ShopIndex;
  onSaved: (next: ShopIndex) => void;
  /** The site's address without the scheme, for the web address field. */
  address: string;
  /** The site's time zone, which suggests a new shop's currency and Stripe country. */
  timeZone?: string | null;
  /** Adding the shop for the first time, in a dialog: no page title, and Cancel / Add shop instead of a save bar. */
  setup?: boolean;
  onCancel?: () => void;
  onDirty?: (dirty: boolean) => void;
}

/** The shop's name, web address and currency. The same form adds the shop (setup) and changes it later. */
export function ShopSettingsView({
  api,
  siteId,
  shop,
  onSaved,
  address,
  timeZone,
  setup = false,
  onCancel,
  onDirty,
}: ShopSettingsViewProps) {
  const [locked, setLocked] = useState(false);
  const [commerceDirty, setCommerceDirty] = useState(false);
  const [name, setName] = useState(shop.settings?.name ?? "Shop");
  const [path, setPath] = useState(shop.settings?.base.slice(1) ?? "shop");
  const [currency, setCurrency] = useState(() => shop.settings?.currency ?? likelyCurrency(timeZone));
  const [undo, setUndo] = useState<ShopSettings | null>(null);
  const currencies = useMemo(() => currencyOptions(shop.settings?.currency), [shop.settings?.currency]);
  const working = useRef(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const dirty =
    !setup &&
    Boolean(shop.settings) &&
    (name !== shop.settings!.name ||
      `/${path}` !== shop.settings!.base ||
      currency !== shop.settings!.currency);
  useEffect(() => {
    onDirty?.(dirty || commerceDirty);
    return () => onDirty?.(false);
  }, [dirty, commerceDirty, onDirty]);

  const run = async (
    settings: ShopSettings,
    refused: string,
    then: (before: ShopSettings | null) => void,
  ) => {
    if (working.current) return; working.current = true;
    setBusy(true);
    setError(null);
    try {
      const before = shop.settings;
      onSaved(await shopRequest(() => api.saveShopSettings(siteId, settings, shop.rowVersion)));
      then(before);
    } catch (caught) {
      setError(caught instanceof ShopRefusedError || caught instanceof ShopTimeoutError ? caught.message : refused);
    } finally {
      working.current = false; setBusy(false);
    }
  };
  const submit = () =>
    run(
      { name, base: `/${path}`, currency },
      "This didn't save. Reload and try again.",
      setUndo,
    );
  const undoSave = () => {
    if (!undo) return;
    void run(undo, "Undo didn't save. Reload and try again.", () => {
      setName(undo.name);
      setPath(undo.base.slice(1));
      setCurrency(undo.currency);
      setUndo(null);
    });
  };
  const discard = () => {
    if (shop.settings) {
      setName(shop.settings.name);
      setPath(shop.settings.base.slice(1));
      setCurrency(shop.settings.currency);
    }
    setError(null);
  };

  return (
    <>
      <form aria-busy={busy}
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <fieldset className="bw-shop-locked bw-area-stack" disabled={busy}>
          {!setup && <ListHead title="Settings" />}
          <Card>
            <Field label="Shop name">
              <input
                required
                maxLength={80}
                value={name}
                onChange={(event) => setName(event.target.value)}
              />
            </Field>
            <Field label="Web address">
              <span className="bw-area-prefixed">
                <span>{address}/</span>
                <input
                  required
                  pattern="[a-z0-9]+(-[a-z0-9]+)*"
                  value={path}
                  onChange={(event) => setPath(event.target.value)}
                />
              </span>
            </Field>
            <Field
              label="Currency"
              hint={
                locked
                  ? "Currency is locked after your first order."
                  : "Before your first order, currency can change. Existing price amounts keep their numbers."
              }
            >
              <Combobox
                disabled={locked}
                value={currency}
                options={currencies}
                onChange={setCurrency}
                placeholder="Search currencies"
              />
            </Field>
          </Card>

          {error && (
            <AreaError message={error} onDismiss={() => setError(null)} />
          )}
          {setup ? (
            <div className="bw-actions">
              {onCancel && (
                <button
                  type="button"
                  className="bw-btn bw-btn--quiet"
                  onClick={onCancel}
                >
                  Cancel
                </button>
              )}
              <button
                className="bw-btn bw-btn--primary bw-btn--action"
                type="submit"
                disabled={busy}
              >
                {busy ? <ShopActivity announce={false} label="Saving…" /> : "Add shop"}
              </button>
            </div>
          ) : dirty ? (
            <SaveBar
              state="dirty"
              busy={busy}
              message="Settings changed · not saved yet"
              onSave={() => void submit()}
              onDiscard={discard}
            />
          ) : (
            undo && <SaveBar state="saved" busy={busy} onUndo={undoSave} />
          )}
        </fieldset>
      </form>
      {!setup && api.commerce && (
        <CommerceSettingsView
          api={api.commerce}
          siteId={siteId}
          currency={shop.settings!.currency}
          timeZone={timeZone}
          onLock={setLocked}
          onDirty={setCommerceDirty}
        />
      )}
    </>
  );
}
